import React, { useState, useCallback, useEffect } from 'react';
import PerformanceGrid from './PerformanceGrid';

const PerformanceDashboard = () => {
  const [vesselName, setVesselName] = useState('');
  const [voyageNumber, setVoyageNumber] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [vesselSuggestions, setVesselSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [portTypeFilter, setPortTypeFilter] = useState('ALL'); // 'ALL', 'POL', 'POD'

  // Debounce function to prevent excessive API calls
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Fetch vessel name suggestions - using existing performance endpoint
  const fetchVesselSuggestions = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setVesselSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // Use existing performance status endpoint to get vessel names
      const response = await fetch(`/api/performance/status?vesselName=${encodeURIComponent(searchTerm)}`);
      const data = await response.json();

      // Extract unique vessel names from the results
      const uniqueVessels = [...new Set(data.performances?.map(p => p.vessel_name) || [])];
      setVesselSuggestions(uniqueVessels);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setVesselSuggestions([]);
    }
  };

  // Debounced vessel search
  const debouncedVesselSearch = useCallback(
    debounce(fetchVesselSuggestions, 300),
    []
  );

  // Handle vessel name input change
  const handleVesselChange = (e) => {
    const value = e.target.value;
    setVesselName(value);
    debouncedVesselSearch(value);
    if (value.length < 2) {
      setShowSuggestions(false);
    }
  };

  // Handle vessel suggestion selection
  const handleVesselSelect = (vessel) => {
    setVesselName(vessel);
    setShowSuggestions(false);
  };

  // Search for vessel/voyage performance data - using existing endpoint
  const handleSearch = async () => {
    if (!vesselName.trim()) {
      alert('Please enter a vessel name');
      return;
    }

    setIsLoading(true);
    setSearchPerformed(true);

    try {
      // Use existing performance status endpoint
      const params = new URLSearchParams();
      params.append('vesselName', vesselName.trim());
      if (voyageNumber.trim()) {
        params.append('voyageNumber', voyageNumber.trim());
      }

      const response = await fetch(`/api/performance/status?${params}`);
      const data = await response.json();

      // Transform the data into the expected format
      if (data.performances && data.performances.length > 0) {
        // Filter by port type if specified
        let performances = data.performances;
        if (portTypeFilter !== 'ALL') {
          performances = data.performances.filter(p => p.port_type === portTypeFilter);
        }

        // Calculate summary statistics based on filtered data
        const totalRecords = performances.length;
        const onTimeCount = performances.filter(p => {
          const delay = p.arrival_delay_minutes;
          if (!delay) return false;
          const delayDays = delay / (24 * 60);
          return delayDays >= -2 && delayDays <= 2; // ±2 days = on time
        }).length;
        const earlyCount = performances.filter(p => {
          const delay = p.arrival_delay_minutes;
          if (!delay) return false;
          return delay < -2 * 24 * 60; // > 2 days early
        }).length;
        const avgDelay = performances.length > 0
          ? performances.reduce((sum, p) => sum + (p.arrival_delay_minutes || 0), 0) / performances.length
          : 0;

        setSearchResults({
          performances: data.performances,
          summary: {
            totalRecords,
            onTimeRate: Math.round((onTimeCount / totalRecords) * 100),
            avgDelay: Math.round(avgDelay),
            earlyArrivals: earlyCount
          }
        });
      } else {
        setSearchResults({
          performances: [],
          summary: {
            totalRecords: 0,
            onTimeRate: 0,
            avgDelay: 0,
            earlyArrivals: 0
          }
        });
      }
    } catch (error) {
      console.error('Error searching vessel data:', error);
      setSearchResults({ error: 'Failed to fetch vessel data' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get delay status styling (updated thresholds: 2/7/14 days)
  const getDelayStatus = (delayMinutes) => {
    if (!delayMinutes) return { status: 'No Data', color: '#9CA3AF' };

    const delayDays = delayMinutes / (24 * 60);

    // Early arrivals
    if (delayMinutes < -2 * 24 * 60) return { status: 'Early', color: '#3B82F6' }; // > 2 days early
    if (delayMinutes < 0) return { status: 'On Time', color: '#10B981' }; // < 2 days early

    // Late arrivals/departures
    if (delayDays <= 2) return { status: 'On Time', color: '#10B981' }; // 0-2 days
    if (delayDays <= 7) return { status: 'Minor Delay', color: '#F59E0B' }; // 2-7 days
    if (delayDays <= 14) return { status: 'Moderate Delay', color: '#F97316' }; // 7-14 days
    return { status: 'Major Delay', color: '#EF4444' }; // > 14 days
  };

  const formatDelay = (minutes) => {
    if (!minutes) return 'N/A';
    const days = Math.floor(Math.abs(minutes) / (24 * 60));
    const hours = Math.floor((Math.abs(minutes) % (24 * 60)) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes > 0 ? '+' : '';
    return `${sign}${days}d ${hours}h ${mins}m`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Format: DD/MM/YYYY HH:mm (SAST)
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center">
          <span className="mr-2">🚢</span>
          Vessel Performance History
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-2">
          Search historical performance data for any vessel and voyage
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          🔵 POL = Port of Loading | 🟢 POD = Port of Discharge
        </p>
      </div>

      {/* Search Box */}
      <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="space-y-4">
          {/* Vessel Name Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Vessel Name
            </label>
            <input
              type="text"
              value={vesselName}
              onChange={handleVesselChange}
              onKeyPress={handleKeyPress}
              placeholder="Start typing vessel name..."
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              autoComplete="off"
            />
            {showSuggestions && vesselSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {vesselSuggestions.map((vessel, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleVesselSelect(vessel)}
                    className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    {vessel}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Voyage Number Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Voyage Number <span className="text-gray-500 dark:text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={voyageNumber}
              onChange={(e) => setVoyageNumber(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., 1234S"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={isLoading || !vesselName.trim()}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isLoading ? 'Searching...' : 'Search Performance History'}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchPerformed && !isLoading && (
        <div className="max-w-6xl mx-auto">
          {searchResults?.error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
              <p className="text-red-600 dark:text-red-400">{searchResults.error}</p>
            </div>
          ) : searchResults?.performances && searchResults.performances.length > 0 ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">Total Records</div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{searchResults.summary?.totalRecords || searchResults.performances.length}</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg shadow p-6 border border-green-200 dark:border-green-800 text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">On-Time Rate</div>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{searchResults.summary?.onTimeRate || 0}%</div>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg shadow p-6 border border-red-200 dark:border-red-800 text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">Avg Delay</div>
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {searchResults.summary?.avgDelay ? formatDelay(searchResults.summary.avgDelay) : 'N/A'}
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow p-6 border border-blue-200 dark:border-blue-800 text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">Early Arrivals</div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{searchResults.summary?.earlyArrivals || 0}</div>
                </div>
              </div>

              {/* Port Type Filter */}
              <div className="mb-6 flex items-center justify-center space-x-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Port Type:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPortTypeFilter('ALL')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      portTypeFilter === 'ALL'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    All Ports
                  </button>
                  <button
                    onClick={() => setPortTypeFilter('POL')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      portTypeFilter === 'POL'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                    }`}
                  >
                    POL Only
                  </button>
                  <button
                    onClick={() => setPortTypeFilter('POD')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      portTypeFilter === 'POD'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                    }`}
                  >
                    POD Only
                  </button>
                </div>
              </div>

              {/* Performance Grid */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Performance Grid for {vesselName}
                    {voyageNumber && ` - Voyage ${voyageNumber}`}
                  </h3>
                </div>
                <div className="p-6">
                  <PerformanceGrid vesselName={vesselName} voyageNumber={voyageNumber} />
                </div>
              </div>
            </>
          ) : (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
              <p className="text-yellow-800 dark:text-yellow-300">
                No performance data found for {vesselName}
                {voyageNumber && ` - Voyage ${voyageNumber}`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Initial Help Text */}
      {!searchPerformed && (
        <div className="max-w-2xl mx-auto text-center text-gray-600 dark:text-gray-400">
          <p>Start typing a vessel name above to search for historical performance data.</p>
          <p className="mt-2 text-sm">Vessel names will appear as you type - no page refresh required!</p>
        </div>
      )}
    </div>
  );
};

export default PerformanceDashboard;